import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  fetchAnalysisSamples,
  sampleCasePath,
  type AnalysisSample,
  type AnalysisSamplesResult,
} from '../features/analyses/analysisSamples'
import { useVariantLensApiClient } from '../features/auth/hooks/useVariantLensApiClient'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; result: AnalysisSamplesResult }
  | { kind: 'unavailable' }
  | { kind: 'ambiguous'; candidateAnalysisIds: string[] }
  | { kind: 'notFound' }
  | { kind: 'invalid'; message: string }
  | { kind: 'error'; message: string }

/**
 * Analysis-samples pick list (GEN-5674).
 *
 * Resolves the pasted analysis token from the route to its member samples via
 * GET /analyses/{token}/samples (GEN-5673) and renders them as a pick list, each
 * row opening the existing case workspace for that sample. Mirrors OrdersList's
 * calm outcome handling: loading, empty, unavailable (503), ambiguous (409, with
 * a candidate chooser), not found (404), invalid (422), and generic error, so a
 * reviewer never sees a raw failure.
 */
const AnalysisSamplesPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const apiClient = useVariantLensApiClient()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })

  useEffect(() => {
    const abort = new AbortController()
    setState({ kind: 'loading' })
    fetchAnalysisSamples(apiClient, token, abort.signal)
      .then((outcome) => {
        if (abort.signal.aborted || outcome.kind === 'aborted') return
        if (outcome.kind === 'ok') setState({ kind: 'ok', result: outcome.result })
        else if (outcome.kind === 'unavailable') setState({ kind: 'unavailable' })
        else if (outcome.kind === 'ambiguous')
          setState({ kind: 'ambiguous', candidateAnalysisIds: outcome.candidateAnalysisIds })
        else if (outcome.kind === 'notFound') setState({ kind: 'notFound' })
        else if (outcome.kind === 'invalid') setState({ kind: 'invalid', message: outcome.message })
        else setState({ kind: 'error', message: outcome.message })
      })
      .catch(() => {
        if (!abort.signal.aborted) setState({ kind: 'error', message: 'Could not resolve the analysis' })
      })
    return () => abort.abort()
  }, [apiClient, token])

  const openSample = (sample: AnalysisSample) => navigate(sampleCasePath(sample))

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-10">
      <header className="flex flex-col gap-2">
        <button
          type="button"
          data-testid="analysis-back"
          onClick={() => navigate('/home')}
          className="text-brand-700 hover:text-brand-800 self-start text-sm underline underline-offset-2"
        >
          Back to search
        </button>
        <h1 className="text-text-heading text-2xl font-semibold tracking-tight">Analysis samples</h1>
        <p className="text-text-subtle font-mono text-sm break-all">{token}</p>
      </header>

      <section
        aria-label="Analysis samples"
        data-testid="analysis-samples"
        className="border-border-subtle w-full rounded-2xl border bg-white p-6 shadow-sm sm:p-8"
      >
        {state.kind === 'loading' && (
          <p data-testid="analysis-loading" className="text-text-subtle text-sm">
            Resolving analysis...
          </p>
        )}

        {state.kind === 'unavailable' && (
          <p data-testid="analysis-unavailable" className="text-text-subtle text-sm">
            Analysis lookup is not available yet. Try again once the order integration is configured.
          </p>
        )}

        {state.kind === 'notFound' && (
          <p data-testid="analysis-notfound" className="text-text-subtle text-sm">
            No analysis matched that id.
          </p>
        )}

        {state.kind === 'invalid' && (
          <p data-testid="analysis-invalid" role="alert" className="text-error-600 text-sm">
            {state.message}
          </p>
        )}

        {state.kind === 'error' && (
          <p data-testid="analysis-error" role="alert" className="text-error-600 text-sm">
            Could not resolve the analysis. Please try again.
          </p>
        )}

        {state.kind === 'ambiguous' && (
          <div data-testid="analysis-ambiguous" className="flex flex-col gap-3">
            <p className="text-text-body text-sm">
              That id matches more than one analysis on the order. Open the specific analysis:
            </p>
            {state.candidateAnalysisIds.length === 0 ? (
              <p className="text-text-subtle text-sm">No candidate analyses were returned.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {state.candidateAnalysisIds.map((id) => (
                  <li key={id}>
                    <button
                      type="button"
                      data-testid="analysis-candidate"
                      onClick={() => navigate(`/analyses/${encodeURIComponent(id)}`)}
                      className="text-brand-700 hover:text-brand-800 font-mono text-sm underline underline-offset-2"
                    >
                      {id}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {state.kind === 'ok' && state.result.samples.length === 0 && (
          <p data-testid="analysis-empty" className="text-text-subtle text-sm">
            This analysis has no samples yet.
          </p>
        )}

        {state.kind === 'ok' && state.result.samples.length > 0 && (
          <ul data-testid="analysis-rows" className="divide-y divide-gray-100">
            {state.result.samples.map((sample) => (
              <li key={sample.sampleId} className="flex items-center justify-between gap-3 py-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-text-heading truncate font-mono text-sm">{sample.name}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    {sample.labSampleId && (
                      <span data-testid="analysis-sample-lab" className="text-text-subtle font-mono text-xs">
                        {sample.labSampleId}
                      </span>
                    )}
                    {sample.role && (
                      <span
                        data-testid="analysis-sample-role"
                        className="border-brand-200 bg-brand-50 text-brand-700 rounded-full border px-2.5 py-0.5 text-xs font-medium"
                      >
                        {sample.role}
                      </span>
                    )}
                    {sample.sex && (
                      <span
                        data-testid="analysis-sample-sex"
                        className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-600"
                      >
                        {sample.sex}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="analysis-sample-open"
                  onClick={() => openSample(sample)}
                  className="bg-brand-600 hover:bg-brand-700 focus:ring-brand-300 shrink-0 rounded-lg px-4 py-1.5 text-sm font-medium text-white shadow-sm transition focus:ring-2 focus:outline-none"
                >
                  Open
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

export default AnalysisSamplesPage
